<template>
  <SMDialog :model-value="showDialog" @update:model-value="closeHandler">
    <template #header>Manage Background Image</template>
    <template #content>
      <div class="flex flex-col gap-2">
        <div v-if="noBackground" class="grid gap-x-1 gap-y-2" style="grid-template-columns: 1fr auto">
          <SMFileInput
            ref="fileInputRef"
            label="Upload Image"
            type="file"
            class="flex-col !items-start"
            accept="*/image"
            @files="onFileSelected"
          />
          <SMButton
            label="Upload"
            class="self-end"
            type="primary"
            filled
            :disabled="!file"
            :loading="uploadLoading"
            @click="onUpload"
          />

          <div v-if="isError" class="col-span-full bg-red-300 px-2 py-1 my-1">An error occured on upload</div>

          <SMInput ref="inputRef" v-model="url" type="url" label="Use Image URL" class="flex-col !items-start" />
          <SMButton
            label="Ok"
            class="self-end"
            type="primary"
            filled
            :disabled="!url"
            :loading="urlLoading"
            @click="onUrlOk"
          />
        </div>

        <label>
          <span>Scaling</span>
          <SMSlider v-model="bgScaling" :min="1" />
        </label>

        <label>
          <span>Transparency</span>
          <SMSlider v-model="bgOpacity" />
        </label>

        <SMCheckbox v-model="showBgOnCharts" @change="onCheckboxToggle">Show background on rendered charts</SMCheckbox>

        <SMButton v-if="!noBackground" label="Clear Background Image" type="danger" filled @click="clearUploadedBg" />
      </div>
    </template>
    <template #actions></template>
  </SMDialog>
</template>

<script setup lang="ts">
import { BG_START_SCALE_RATE, DRAWINGMODE, OPACITY_START_SCALE_RATE } from '@/constants';
import Application from '@/core/application';
import { useActions, useState } from '@/store/useStore';
import eventbus from 'tiny-emitter/instance';
import { onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import SMButton from '../ui/SMButton.vue';
import SMFileInput from '../ui/SMFileInput.vue';
import SMInput from '../ui/SMInput.vue';
import SMSlider from '../ui/SMSlider.vue';
import SMCheckbox from '../ui/legacy/SMCheckbox.vue';
import SMDialog from '../ui/legacy/SMDialog.vue';
import { computed } from 'vue';

const app = Application.instance;
const seatmap = reactive(useState('seatmap'));

const isError = ref(false);
const urlLoading = ref(false);
const uploadLoading = ref(false);
const showBgOnCharts = ref(true);
const showDialog = ref(true);
const bgImageUpload = ref<string>();
const bgImageUrl = ref<string>();
const url = ref<string>();

const bgScaling = ref((seatmap.bgImageScale ?? 1) * 100);
const bgOpacity = ref((seatmap.bgImageOpacity ?? 1) * 100);

const noBackground = computed(() => !bgImageUpload.value && !bgImageUrl.value);

const { updateBgImage, updateIsBgUploaded, updateBgImageScale, updateBgImageOpacity, updateBgImageShowInRenderer } =
  useActions('seatmap');

const { changeDrawingMode, setSelection } = useActions('settings');

const setupOnDragAction = () => {
  if (app.bgImage) {
    //app.bgImage.front();
    // @ts-expect-error
    app.bgImage.draggable();
    app.bgImage.on('dragstart', bgImageDragHandle);
    app.bgImage.on('dragend', bgImageDragHandle);
  }
};

const destroyOnDragAction = () => {
  if (app.bgImage) {
    //app.bgImage.back();
    app.bgImage.off('dragstart', bgImageDragHandle);
    app.bgImage.off('dragend', bgImageDragHandle);
    // @ts-expect-error
    app.bgImage.draggable(false);
  }
};

const onError = () => {
  isError.value = true;
  clearBg();
};

const file = ref<File>();

const onFileSelected = (files: File[]) => {
  file.value = files[0];
};

const onUpload = () => {
  uploadLoading.value = true;

  const fr = new FileReader();
  fr.onload = (ev) => {
    const uri = fr.result as string;

    const imageUpload = app.props.onImageUpload?.(uri);
    if (imageUpload) {
      clearBg();
      updateIsBgUploaded(true);
      eventbus.emit('background:add', imageUpload, true);
      bgImageUpload.value = imageUpload;
      setupOnDragAction();
    } else {
      onError();
    }
  };

  if (file.value) {
    fr.readAsDataURL(file.value);
  }
};

const bgImageDragHandle = (e: Event) => {
  if (e.type === 'dragstart') {
    app.seatmap.triggerUnsavedChanges(true);
    showDialog.value = false;
  } else {
    eventbus.emit('selection:delayedHistory', 'Background Image repositioned');
    showDialog.value = true;
  }
};

const clearUploadedBg = () => {
  app.props?.onImageDelete?.();
  clearBg(true);
};

const fileInputRef = ref<InstanceType<typeof SMFileInput>>();
const clearBg = (uploaded = false) => {
  eventbus.emit('background:delete');
  updateBgImage(null);
  updateBgImageScale(BG_START_SCALE_RATE);
  updateBgImageOpacity(OPACITY_START_SCALE_RATE);

  bgImageUpload.value = undefined;
  bgImageUrl.value = undefined;
  urlLoading.value = false;
  file.value = undefined;
  url.value = undefined;

  if (fileInputRef.value?.input) {
    fileInputRef.value.input.value = '';
  }
};

const inputRef = ref<InstanceType<typeof SMInput>>();
const onUrlOk = () => {
  isError.value = false;

  if (!inputRef.value?.reportValidity()) return;

  urlLoading.value = true;
  eventbus.emit('background:add', url.value);
};

const onImageLoaded = (url: string, uploaded = false) => {
  if (uploaded) {
    bgImageUpload.value = url;
  } else {
    bgImageUrl.value = url;
  }
  urlLoading.value = false;
  uploadLoading.value = false;
  updateBgImage(url);
};

watch(bgScaling, (value) => {
  updateBgImageScale(value / 100);
  eventbus.emit('background:resize', value / 100);
});

watch(bgOpacity, (value) => {
  updateBgImageOpacity(value / 100);
  eventbus.emit('background:maketransparent', value / 100);
});

const onCheckboxToggle = (val: boolean) => {
  updateBgImageShowInRenderer(val);
};

const closeHandler = (val: boolean) => {
  if (!val) {
    changeDrawingMode(DRAWINGMODE.MAIN);
    setSelection(true);
  }
};

onBeforeUnmount(() => {
  eventbus.off('background:error', onError);
  eventbus.off('background:loaded', onImageLoaded);
  destroyOnDragAction();
});

onMounted(() => {
  if (seatmap.isBgUploaded) {
    bgImageUpload.value = app.bgImage ? app.bgImage.src : undefined;
  } else {
    bgImageUrl.value = app.bgImage ? app.bgImage.src : undefined;
  }
  eventbus.emit('menu:hide');
  eventbus.emit('selection:cleared');
  eventbus.on('background:error', onError, this);
  eventbus.on('background:loaded', onImageLoaded, this);
  setupOnDragAction();
});
</script>
